<script setup lang="ts">
import { useElementBounding } from '@vueuse/core';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { computed, markRaw, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import LargeMenu from '@/components/layout/header/desktop/LargeMenu.vue';
import NavHeader from '@/components/layout/header/desktop/NavHeader.vue';
import { useRouterLinks } from '@/composables/navigation/useRouterLinks';
import { useOverlay } from '@/stores/overlay';
import {
  sendNavigationMenuEngagement,
  sendNavigationMenuImpression,
} from '@/utils/analytics/navigation';
import { desktopAttribution, MenuByType, SiteNavigation } from '@/utils/navMenu';

const props = defineProps<{ siteNavigation: SiteNavigation }>();

const navList = ref<HTMLElement>();
const { height } = useElementBounding(navList);
const subMenuOffset = computed(() => `${height.value ? height.value : 0}px`);

const mainNav = computed<MenuByType[]>(() =>
  props.siteNavigation.categories.map(({ menu }) => ({ ...menu, type: 'LargeMenu' })),
);

const activeMenu = ref<MenuByType>();

let showSubMenuTimerId: ReturnType<typeof setTimeout> | undefined;

const largeMenuList = ref<HTMLElement>();
const { activate, deactivate } = useFocusTrap(largeMenuList, {
  clickOutsideDeactivates: true,
  onDeactivate: () => {
    clearTimeout(showSubMenuTimerId);
    activeMenu.value = undefined;
  },
});

const hideSubMenu = () => {
  clearTimeout(showSubMenuTimerId);
  activeMenu.value = undefined;
  deactivate();
};

const activateMenu = (rawNavEntry: MenuByType) => {
  if (activeMenu.value === rawNavEntry) return;

  activeMenu.value = rawNavEntry;

  sendNavigationMenuImpression({
    menu_name: rawNavEntry.header.text,
    source: 'Desktop Mega Menu',
  });
};

const selectSubMenu = (navEntry: MenuByType, skipDelay = true) => {
  clearTimeout(showSubMenuTimerId);

  const rawNavEntry = markRaw(navEntry);

  // if going directly from one NavHeader to another, show the new menu immediately
  if (skipDelay) {
    activateMenu(rawNavEntry);
  } else {
    showSubMenuTimerId = setTimeout(() => {
      activateMenu(rawNavEntry);
    }, 300);
  }
};

const focusIntoMenu = async (navEntry: MenuByType) => {
  selectSubMenu(navEntry);
  await nextTick();
  activate();
};

const onNavLinkClicked = (navEntry: MenuByType, e: Event) => {
  if (navEntry.header.url) {
    sendNavigationMenuEngagement({
      link_text: navEntry.header.text,
      menu_name: navEntry.header.text,
      source: 'Desktop Mega Menu',
    });
    return;
  }
  e.preventDefault();
  e.stopPropagation();
};

const overlay = useOverlay();
watch(activeMenu, () => (activeMenu.value ? overlay.show(true) : overlay.hide()));

useRouterLinks(navList, useRouter());
</script>

<template>
  <div class="bg-white">
    <nav aria-label="main" class="relative mx-auto" data-test="mega-menu">
      <ul
        class="flex justify-between min-w-[800px] w-fit mx-auto"
        ref="navList"
        @mouseleave="hideSubMenu"
        role="menubar"
      >
        <li v-for="navEntry in mainNav" :key="navEntry.header.url" @click="hideSubMenu" role="none">
          <a
            v-bind="desktopAttribution(navEntry)"
            aria-controls="large-menu"
            :aria-expanded="activeMenu === navEntry"
            aria-haspopup="true"
            class="block p-3 hover:no-underline focus:no-underline"
            data-test="mega-menu-link"
            :href="navEntry.header.url"
            role="menuitem"
            @click="onNavLinkClicked(navEntry, $event)"
            @keydown.prevent.down="focusIntoMenu(navEntry)"
            @mouseover="selectSubMenu(navEntry, !!activeMenu)"
          >
            <div class="flex items-center">
              <NavHeader
                :activeState="activeMenu && (activeMenu === navEntry ? 'active' : 'suppressed')"
                class="gap-x-2.5"
                :header="navEntry.header"
              />
            </div>
          </a>
          <div ref="largeMenuList">
            <LargeMenu
              v-if="navEntry.type === 'LargeMenu'"
              v-show="activeMenu === navEntry"
              class="absolute inset-x-0 pt-5 pb-8 w-full border-b border-solid shadow-md sub-menu-offset border-nuts-neutral-200 shadow-black/10"
              :id="`${navEntry.header.text || navEntry.header.url}-large-menu`"
              :menu="navEntry"
              @close="hideSubMenu"
            />
          </div>
        </li>
      </ul>
    </nav>
  </div>
</template>

<style lang="scss" scoped>
.sub-menu-offset {
  top: v-bind('subMenuOffset');
}
</style>
